import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PINSAIAssistantLabel from '../../../../../../../../components/PINSAIAssistantLabel';
import { FailedGptRequestStatuses, GptRequestStatuses } from '../../../../../../../../constants/gptRequestStatuses';
import { createErrorToast, createSuccessToast } from '../../../../../../../../redux/modules/app';
import { hideModal } from '../../../../../../../../redux/modules/modal';
import recordKeys from '../../../../../../../../services/models/record/keys';
import { useGptRequest } from '../../../../../../../../services/record-service';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import { queryClient } from '../../../../../../../../utils/queryClientUtil';

const StoppedGptRequestStatuses = [GptRequestStatuses.Cancelled, GptRequestStatuses.Deleted];

const failureDescription = ({ status, error }) => {
  switch (status) {
    case GptRequestStatuses.DocumentSizeTooLarge:
      return error || 'One or more documents exceed the scan limits.';
    case GptRequestStatuses.NoQuestionsContent:
      return 'This record has no questions to scan.';
    case GptRequestStatuses.NoDocumentsContent:
      return 'No documents were selected to scan.';
    default:
      return 'Please wait a minute and try to rescan.';
  }
};

const ScanProgressBar = ({ recordId, gptRequestId }) => {
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');

  // fetch the gpt request with polling
  const {
    data: gptRequest,
    isLoading: gptRequestLoading,
    isFetching: gptRequestFetching,
    isRefetching: gptRequestRefetching
  } = useGptRequest(
    {
      recordId,
      gptRequestId,
      options: {
        refetchInterval: data => {
          if (!data) return 2000;

          if (FailedGptRequestStatuses.includes(data.status)) return false;
          if (StoppedGptRequestStatuses.includes(data.status)) return false;
          if (data.status === GptRequestStatuses.Published) return false;

          // keep polling otherwise
          return 1500;
        }
      }
    }
  );

  // handle invalidations and exiting modal from gpt request status
  useEffect(() => {
    const status = gptRequest?.status;
    const isStopped = StoppedGptRequestStatuses.includes(status);

    if (FailedGptRequestStatuses.includes(status) || isStopped) {
      const isTooLarge = status === GptRequestStatuses.DocumentSizeTooLarge;

      dispatch(
        createErrorToast(isStopped
          ? { title: 'Scan canceled' }
          : {
            title: 'Scan failed',
            description: failureDescription(gptRequest),
            ...(isTooLarge && { duration: 10000 })
          })
      );

      queryClient.invalidateQueries(recordKeys.recordGptRequests(recordId));
      queryClient.invalidateQueries(recordKeys.recordQuestionRequirements(recordId));
      dispatch(hideModal());
      return;
    }

    switch (status) {
      case GptRequestStatuses.Published:
        dispatch(hideModal());
        dispatch(createSuccessToast({ title: 'Ai Scan Completed!' }));
        queryClient.invalidateQueries(recordKeys.recordGptRequests(recordId));
        queryClient.invalidateQueries(recordKeys.recordQuestionRequirements(recordId));
        break;
      default:
        break;
    }
  }, [gptRequest, gptRequestLoading]);

  // update text and progress bar values based on refetching gpt request status
  // necessary to separate this from the polling effect above, else infinite loop on invalidations
  useEffect(() => {
    if (gptRequestLoading) {
      setProgress(1);
      setStatusText('Creating request...');
      return;
    }

    if (FailedGptRequestStatuses.includes(gptRequest?.status)) {
      setProgress(0);
      setStatusText('Request failed');
      return;
    }

    if (StoppedGptRequestStatuses.includes(gptRequest?.status)) {
      setProgress(0);
      setStatusText('Request canceled');
      return;
    }

    switch (gptRequest?.status) {
      case GptRequestStatuses.Created: {
        const value = Math.min(Math.max(1, progress + 1), 20);

        if (value >= 10 && value <= 20) {
          setStatusText('Preparing documents for scan...');
        } else {
          setStatusText('Creating request...');
        }

        setProgress(value);
        break;
      }

      case GptRequestStatuses.Processing: {
        const value = Math.min(Math.max(40, progress + 1), 70);

        if (value >= 50 && value <= 60) {
          setStatusText('Parsing scan...');
        } else if (value >= 60 && value <= 70) {
          setStatusText('Crossing t\'s and dotting i\'s...');
        } else {
          setStatusText('Scanning documents...');
        }

        setProgress(value);
        break;
      }

      case GptRequestStatuses.Retrying:
        setProgress(p => Math.min(Math.max(40, p), 70));
        setStatusText('The AI service is busy. Retrying...');
        break;

      case GptRequestStatuses.Received:
        setProgress(p => Math.min(Math.max(60, p + 1), 80));
        setStatusText('Processing results...');
        break;

      case GptRequestStatuses.Published:
        setProgress(100);
        setStatusText('Request completed!');
        break;

      default:
        break;
    }
  }, [gptRequestLoading, gptRequestRefetching]);

  // a third increment on interval value to animate the progress bar more smoothly
  useEffect(() => {
    if (progress >= 100) return; // stop when complete

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev < 99) {
          return prev + 1; // increase by 1
        }

        return prev; // cap it until "Completed"
      });
    }, 500);

    return () => clearInterval(interval);
  }, [progress]);

  return (
    <Box my={8}>
      <PINSAIAssistantLabel mb={10} />
      <Box mb={4} w="100%">
        {/* Outer track */}
        <Box
          bg="gray.200"
          borderRadius="full"
          boxShadow="inset 0 1px 3px rgba(0,0,0,0.2)"
          h="20px"
          overflow="hidden"
          w="100%"
        >
          {/* Inner bar */}
          <Box
            bgGradient={colors.gradient.brandPrimary} // 🔥 custom color scheme
            borderRadius="full"
            h="100%"
            transition="width 0.8s ease-in-out" // ⚡ smoother transitions
            w={`${progress}%`}
          />
        </Box>

        {statusText && (
          <Text color="gray.600" fontSize="sm" mt={2}>
            {statusText}
          </Text>
        )}
      </Box>
    </Box>
  );
};

ScanProgressBar.propTypes = {
  recordId: PropTypes.number,
  gptRequestId: PropTypes.number
};

export default ScanProgressBar;
